import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { showResponseMessage } from "../../redux/AppAction";
import { setY } from '../../redux/AppAction.js';
import { useTranslation } from 'react-i18next';
import ApiEngine from '../../util/ApiEngine';
import Spinner from 'react-bootstrap/Spinner';

const DynamicLoadTable3 = ({
    apiQuery,
    pageSize = 20,
    searchEnabled = true,
    isTable = true,
    headers,
    render,
    onComplete,
    infiniteLoadEnable = true,
    autoscrollEnabled = false,
    defaultSortColumn = 'username',
    defaultSortDirection = 'asc'
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const y = useSelector((state) => state.appState.y);

    const [dataList, setDataList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [sortColumn, setSortColumn] = useState(defaultSortColumn);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortedData = useMemo(() => {
        return [...dataList].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
            if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [dataList, sortColumn, sortDirection]);

    useEffect(() => {
        if (apiQuery && searchEnabled) {
            getDataList();
        }
    }, [apiQuery, searchEnabled]);

    useEffect(() => {
        if (infiniteLoadEnable) {
            window.addEventListener("scroll", onScrollHandler);
            return () => window.removeEventListener("scroll", onScrollHandler);
        }
    }, [dataList, totalRecords, isLoading]);

    const onScrollHandler = () => {
        const windowHeight = window.innerHeight;
        const docHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
        );
        const windowBottom = windowHeight + Math.ceil(window.pageYOffset);

        if (autoscrollEnabled && window.scrollY > 0) {
            dispatch(setY(window.scrollY));
        }

        if (windowBottom >= docHeight && !isLoading && dataList.length < totalRecords) {
            getDataList();
        }
    };

    const getDataList = async () => {
        if (!searchEnabled) {
            setDataList([]);
            setTotalRecords(0);
            return;
        }

        setIsLoading(true);
        const pageNumber = Math.ceil(dataList.length / pageSize);
        const isQueryStringAppended = apiQuery.includes('?');
        let apiUrl = `${apiQuery}${isQueryStringAppended ? '&' : '?'}count=${pageSize}&start=${pageNumber * pageSize}`;

        try {
            const responseJson = await ApiEngine.get(apiUrl);
            setIsLoading(false);

            if (responseJson.success) {
                const newData = [...dataList, ...responseJson.data.data];
                setDataList(newData);
                setTotalRecords(responseJson.data.totalCount);

                if (onComplete) {
                    onComplete({ totalDataLoaded: newData.length });
                }

                if (autoscrollEnabled && window.scrollY === 0 && y > 0) {
                    window.scrollTo(0, y);
                }
            } else {
                dispatch(showResponseMessage(responseJson.success, t(responseJson.message)));
            }
        } catch (error) {
            setIsLoading(false);
            dispatch(showResponseMessage(false, t('ERROR_FETCHING_DATA')));
        }
    };

    const renderHeader = () => {
        if (!headers || headers.length === 0) return null;
        return (
            <thead>
                <tr>
                    {headers.map((header, index) => (
                        <th key={index} onClick={() => handleSort(header.key)}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: '' }}>
                                <span>{header.label}</span>
                                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '5px', width: '10px' }}>
                                    <div
                                        style={{
                                            width: 0,
                                            height: 0,
                                            borderLeft: '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            borderBottom: `5px solid ${sortColumn === header.key && sortDirection === 'asc' ? '#fff' : '#999'}`,
                                            marginBottom: '2px'
                                        }}
                                    />
                                    <div
                                        style={{
                                            width: 0,
                                            height: 0,
                                            borderLeft: '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            borderTop: `5px solid ${sortColumn === header.key && sortDirection === 'desc' ? '#fff' : '#999'}`
                                        }}
                                    />
                                </div>
                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
        );
    };

    return (
        <>
            {renderHeader()}
            <tbody>
                {sortedData.length === 0 && !isLoading && (
                    <tr><td colSpan={headers.length} className="font-thick">{t("NO_DATA_FOUND")}</td></tr>
                )}
                {sortedData.map((data, i) => render({ ...data, dataLength: sortedData.length }, i))}
            </tbody>
            {isLoading && (
                <tr style={{ textAlign: "center" }}>
                    <td colSpan={headers.length} style={{ paddingTop: "25px", paddingBottom: "20px" }}>
                        <Spinner className="dynamic-table-spinner" animation="border" role="status" size="sm" style={{ verticalAlign: "middle", width: "2rem", height: "2rem" }}>
                            <span className="sr-only">{t("LOADING")}...</span>
                        </Spinner>
                    </td>
                </tr>
            )}
            {!isLoading && sortedData.length < totalRecords && (
                <tr style={{ textAlign: "center" }}>
                    <td colSpan={headers.length}>
                        <div className="see-more-text" onClick={getDataList}>{t("SEE_MORE")}</div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default DynamicLoadTable3;